// Theme configuration and utilities
export const themes = {
  light: {
    primary: '#4A90E2',
    primaryHover: '#357ABD',
    secondary: '#A1C4FD',
    background: '#FFFFFF',
    backgroundSecondary: '#F7FAFC',
    backgroundCard: '#FFFFFF',
    surface: '#FFFFFF',
    text: '#2D3748',
    textSecondary: '#4A5568',
    textMuted: '#718096',
    border: '#E2E8F0',
    borderLight: '#F1F5F9',
    shadow: 'rgba(0, 0, 0, 0.1)',
    success: '#48BB78',
    warning: '#ED8936',
    error: '#F56565',
    info: '#4299E1',
  },
  dark: {
    primary: '#60A5FA',
    primaryHover: '#3B82F6',
    secondary: '#374151',
    background: '#1A202C',
    backgroundSecondary: '#2D3748',
    backgroundCard: '#2D3748',
    surface: '#374151',
    text: '#F7FAFC',
    textSecondary: '#CBD5E0',
    textMuted: '#A0AEC0',
    border: '#4A5568',
    borderLight: '#2D3748',
    shadow: 'rgba(0, 0, 0, 0.3)',
    success: '#68D391',
    warning: '#F6AD55',
    error: '#FEB2B2',
    info: '#63B3ED',
  },
};

export type ThemeMode = 'light' | 'dark';
export type Theme = typeof themes.light;

export const applyTheme = (mode: ThemeMode) => {
  const theme = themes[mode];
  const root = document.documentElement;
  
  Object.entries(theme).forEach(([key, value]) => {
    root.style.setProperty(`--theme-${key}`, value);
  });
  
  // También actualizar variables CSS existentes para compatibilidad
  root.style.setProperty('--primary-color', theme.primary);
  root.style.setProperty('--background-primary', theme.background);
  root.style.setProperty('--background-secondary', theme.backgroundSecondary);
  root.style.setProperty('--text-primary', theme.text);
  root.style.setProperty('--text-secondary', theme.textSecondary);
  root.style.setProperty('--border-color', theme.border);
  // Inject global CSS to force padding-block: 0 across the app
  const globalStyleId = 'crm-global-padding-block-override';
  let globalStyle = document.getElementById(globalStyleId) as HTMLStyleElement | null;
  const css = `
    @media (max-width: 767.98px) {
      :root,
      html,
      body,
      #root,
      .ant-pro-layout .ant-pro-layout-content {
        padding-block: 0 !important;
        padding-inline: 0 !important;
      }
    }
  `;
  if (!globalStyle) {
    globalStyle = document.createElement('style');
    globalStyle.id = globalStyleId;
    globalStyle.type = 'text/css';
    globalStyle.appendChild(document.createTextNode(css));
    document.head.appendChild(globalStyle);
  } else {
    globalStyle.textContent = css;
  }
  // Actualizar la clase del body para antd
  document.body.setAttribute('data-theme', mode);
  
  // Para Ant Design theme switching
  if (mode === 'dark') {
    document.body.classList.add('dark-theme');
  } else {
    document.body.classList.remove('dark-theme');
  }
};

export const getStoredTheme = (): ThemeMode => {
  const stored = localStorage.getItem('crm-theme');
  return (stored === 'dark' || stored === 'light') ? stored : 'light';
};

export const setStoredTheme = (mode: ThemeMode) => {
  localStorage.setItem('crm-theme', mode);
};
